import type { ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

export type AlertTone = 'error' | 'warning' | 'success' | 'info';

export interface AlertProps {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  /**
   * How assertively the message is announced. `assertive` (the default for
   * errors) interrupts the screen reader; `polite` waits for a pause.
   */
  live?: 'assertive' | 'polite' | 'off';
  className?: string;
}

const TONES: Record<AlertTone, string> = {
  // -subtle backgrounds with -text foregrounds: the raw --danger/--success hexes
  // are fill colours and fail AA as text. See tokens.css.
  error: 'bg-[var(--danger-subtle)] text-[var(--danger-text)]',
  warning: 'bg-[var(--warning-subtle)] text-[var(--warning-text)]',
  success: 'bg-[var(--success-subtle)] text-[var(--success-text)]',
  info: 'bg-[var(--brand-subtle)] text-[var(--brand)]'
};

/**
 * A form-level or screen-level message.
 *
 * Errors are `role="alert"` with `aria-live="assertive"`, so a failed sign-in is
 * announced rather than silently appearing above the fold. WalletFront pushed
 * every failure into a global popup with a numeric status id and no live region
 * at all, which meant a screen-reader user learned nothing.
 */
export function Alert({ tone = 'error', title, children, live, className }: AlertProps) {
  const politeness = live ?? (tone === 'error' ? 'assertive' : 'polite');

  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      aria-live={politeness}
      className={cn(
        'flex flex-col gap-1 rounded-md px-3.5 py-3 text-body-sm',
        TONES[tone],
        className
      )}
    >
      {title !== undefined && <p className="font-semibold">{title}</p>}
      {children !== undefined && <div className="[&_p]:mt-0">{children}</div>}
    </div>
  );
}

/**
 * A live region that is always mounted, so the FIRST error is announced too.
 *
 * A `role="alert"` node that is conditionally rendered is frequently missed:
 * some screen readers only announce changes to regions that existed when the
 * page settled. Keeping the region mounted and swapping its text content is the
 * reliable pattern. It also reserves no height when empty, so nothing jumps.
 */
export function FormError({
  message,
  className
}: {
  message?: string | undefined;
  className?: string;
}) {
  return (
    <p
      role="alert"
      aria-live="assertive"
      className={cn('text-body-sm text-[var(--danger-text)] empty:hidden', className)}
    >
      {message ?? ''}
    </p>
  );
}
